import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { ChevronLeft, ChevronRight, ImageOff, LoaderCircle, Minus, Plus, RefreshCw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { PHOTO, REPROCESS_PHOTO } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import type { PhotoUi } from '../lib/permissions'
import type { Photo } from '../types'
import DeletePhotosDialog from './DeletePhotosDialog'
import PhotoDetailsPanel from './PhotoDetailsPanel'

const MIN_SCALE = 1
const MAX_SCALE = 5

interface Props {
  projectId: string
  photoId: string | null
  orderedIds: string[]
  onNavigate: (id: string) => void
  onClose: () => void
  ui: PhotoUi
  /** Omitted until the defect modal is wired: the action is then hidden. */
  onCreateDefect?: (photoIds: string[]) => void
  onAttachToSnag?: (photoIds: string[]) => void
  onShare?: (photoIds: string[]) => void
}

const isTyping = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
}

const clamp = (n: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, n))

const PhotoViewer: React.FC<Props> = ({ projectId, photoId, orderedIds, onNavigate, onClose, ui, onCreateDefect, onAttachToSnag, onShare }) => {
  const { t } = useTranslation()
  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 })
  const [deleting, setDeleting] = useState(false)
  const [gone, setGone] = useState<string[]>([])
  const drag = useRef<{ x: number; y: number } | null>(null)
  const { data, loading, error, startPolling, stopPolling } = useQuery<{ photo: Photo }>(PHOTO, { variables: { id: photoId }, skip: !photoId })
  const [reprocess, { loading: reprocessing }] = useMutation(REPROCESS_PHOTO, { context: { handlesErrors: true } })

  const photo = data?.photo && data.photo.id === photoId ? data.photo : null
  const ids = orderedIds.filter((id) => gone.indexOf(id) < 0 || id === photoId)
  const index = photoId ? ids.indexOf(photoId) : -1
  const prevId = index > 0 ? ids[index - 1] : null
  const nextId = index >= 0 && index < ids.length - 1 ? ids[index + 1] : null

  const processing = photo?.status === 'PROCESSING'
  useEffect(() => {
    if (!photoId || !processing) return undefined
    startPolling(3000)
    return () => stopPolling()
  }, [photoId, processing]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setZoom({ scale: 1, x: 0, y: 0 })
  }, [photoId])

  useEffect(() => {
    if (!photoId || deleting) return undefined
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return
      if (e.key === 'ArrowLeft' && prevId) onNavigate(prevId)
      if (e.key === 'ArrowRight' && nextId) onNavigate(nextId)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [photoId, prevId, nextId, deleting, onNavigate])

  const zoomBy = (delta: number) =>
    setZoom((z) => {
      const scale = clamp(z.scale + delta)
      return scale === MIN_SCALE ? { scale, x: 0, y: 0 } : { ...z, scale }
    })

  const doReprocess = async () => {
    if (!photo) return
    try {
      await reprocess({ variables: { id: photo.id } })
    } catch (e: any) {
      toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))
    }
  }

  const afterDelete = () => {
    if (photoId) setGone((g) => g.concat(photoId))
    const fallback = nextId ?? prevId
    if (fallback) onNavigate(fallback)
    else onClose()
  }

  const stage = () => {
    if (!photo && (error || !loading)) {
      return (
        <div className="flex flex-col items-center gap-3 text-white/80">
          <ImageOff className="h-8 w-8" />
          <span className="text-sm">{t('Photos_viewer_not_found')}</span>
          <Button variant="outline" size="sm" onClick={onClose}>{t('Common_close')}</Button>
        </div>
      )
    }
    if (!photo) return <LoaderCircle className="h-8 w-8 animate-spin text-white/70" />
    // Server-resolved: author or admin, FAILED or stuck in PROCESSING for over 10 minutes.
    const reprocessButton = photo.canReprocess && (
      <Button variant="outline" size="sm" disabled={reprocessing} onClick={doReprocess}>
        {reprocessing ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        {t('Photos_reprocess')}
      </Button>
    )
    if (photo.status === 'PROCESSING') {
      return (
        <div className="flex flex-col items-center gap-2 text-white/80">
          <LoaderCircle className="h-8 w-8 animate-spin" />
          <span className="text-sm">{t('Photos_processing')}</span>
          {reprocessButton}
        </div>
      )
    }
    if (photo.status === 'FAILED' || !photo.displayUrl) {
      return (
        <div className="flex flex-col items-center gap-3 text-white/80">
          <ImageOff className="h-8 w-8" />
          <span className="text-sm">{t('Photos_processing_failed')}</span>
          {reprocessButton}
        </div>
      )
    }
    return (
      <img
        src={photo.displayUrl}
        alt={photo.title || photo.filename || ''}
        draggable={false}
        className="max-h-full max-w-full select-none object-contain"
        // ui-drift: dynamic
        style={{ transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`, cursor: zoom.scale > 1 ? 'grab' : 'zoom-in' }}
        onDoubleClick={() => setZoom((z) => (z.scale > 1 ? { scale: 1, x: 0, y: 0 } : { ...z, scale: 2.5 }))}
      />
    )
  }

  return (
    <Dialog open={!!photoId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        hideClose
        aria-describedby={undefined}
        className="flex h-screen w-screen max-h-none max-w-none gap-0 overflow-hidden border-0 p-0 sm:rounded-none"
        onEscapeKeyDown={(e) => isTyping(e.target) && e.preventDefault()}
      >
        <DialogTitle className="sr-only">{photo?.title || photo?.filename || t('Photos_module_name')}</DialogTitle>
        <div
          className="relative flex min-w-0 flex-1 items-center justify-center overflow-hidden bg-black"
          onWheel={(e) => zoomBy(e.deltaY < 0 ? 0.25 : -0.25)}
          onMouseDown={(e) => { if (zoom.scale > 1) drag.current = { x: e.clientX - zoom.x, y: e.clientY - zoom.y } }}
          onMouseMove={(e) => { if (drag.current) setZoom({ ...zoom, x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }) }}
          onMouseUp={() => { drag.current = null }}
          onMouseLeave={() => { drag.current = null }}
        >
          {stage()}
          <Button variant="secondary" size="sm" className="absolute right-3 top-3 h-8 w-8 p-0" title={t('Common_close')} onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
          {prevId && (
            <Button variant="secondary" size="sm" className="absolute left-3 top-1/2 h-9 w-9 -translate-y-1/2 p-0" title={t('Photos_previous')} onClick={() => onNavigate(prevId)}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
          )}
          {nextId && (
            <Button variant="secondary" size="sm" className="absolute right-3 top-1/2 h-9 w-9 -translate-y-1/2 p-0" title={t('Photos_next')} onClick={() => onNavigate(nextId)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
          )}
          {photo?.status === 'READY' && photo.displayUrl && (
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-md bg-background/90 p-1">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title={t('Photos_zoom_out')} disabled={zoom.scale <= MIN_SCALE} onClick={() => zoomBy(-0.5)}>
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-12 text-center text-xs tabular-nums">{Math.round(zoom.scale * 100)}%</span>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title={t('Photos_zoom_in')} disabled={zoom.scale >= MAX_SCALE} onClick={() => zoomBy(0.5)}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        <aside className="w-80 shrink-0 overflow-y-auto border-l bg-background">
          {photo && (
            <PhotoDetailsPanel
              key={photo.id}
              projectId={projectId}
              photo={photo}
              ui={ui}
              onCreateDefect={onCreateDefect}
              onAttachToSnag={onAttachToSnag}
              onShare={onShare}
              onDelete={() => setDeleting(true)}
            />
          )}
        </aside>
        {photo && (
          <DeletePhotosDialog projectId={projectId} photoIds={[photo.id]} inUseCount={photo.inUse ? 1 : 0} open={deleting} onOpenChange={setDeleting} onDeleted={afterDelete} />
        )}
      </DialogContent>
    </Dialog>
  )
}

export default PhotoViewer
